<template>
    <Card pageTitle="Price Adjustment Details">
        <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_ADJUSTMENT">
            <BackNavigateBtn title="Back to Price Adjustments" to="/inventory-adjustments/price" />

            <div v-if="loading" class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
                <p class="mt-2">Loading adjustment details...</p>
            </div>

            <div v-else>
                <div class="adjustment-header mb-4">
                    <div class="d-flex justify-content-between flex-wrap">
                        <div>
                            <h3 class="mb-1">{{ adjustment.name }}</h3>
                            <p class="text-secondary mb-0">Created on {{ adjustment.formattedCreatedAt }}</p>
                            <p class="text-secondary mb-0" v-if="adjustment.createdBy">Created by: {{ adjustment.createdBy }}</p>
                        </div>
                        <div class="adjustment-summary">
                            <div class="summary-item">
                                <span>Total Items:</span>
                                <strong>{{ adjustment.totalItems }}</strong>
                            </div>
                            <div class="summary-item">
                                <span>Price Increase:</span>
                                <strong class="text-success">${{ adjustment.priceIncrease }}</strong>
                            </div>
                            <div class="summary-item">
                                <span>Price Decrease:</span>
                                <strong class="text-danger">${{ adjustment.priceDecrease }}</strong>
                            </div>
                            <div class="summary-item">
                                <span>Net Change:</span>
                                <strong :class="adjustment.netChange >= 0 ? 'text-success' : 'text-danger'">
                                    ${{ adjustment.netChange }}
                                </strong>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mb-3">
                    <h4>Adjusted Products</h4>
                    <CustomTable :noFixedWidth="true" style="font-size: 0.9em">
                        <template #head>
                            <tr>
                                <th>Product Name</th>
                                <th>SKU</th>
                                <th>Warehouse</th>
                                <th>Previous Price</th>
                                <th>New Price</th>
                                <th>Change</th>
                                <th>Change (%)</th>
                                <th>Reason</th>
                                <th>Notes</th>
                                <th>Adjusted By</th>
                            </tr>
                        </template>
                        <template #body>
                            <tr v-for="item in adjustmentItems" :key="item.id">
                                <td>{{ item.item_name }}</td>
                                <td>{{ item.item_code }}</td>
                                <td>{{ item.warehouse ? item.warehouse.name : '' }}</td>
                                <td>${{ item.current_price.toFixed(2) }}</td>
                                <td>${{ item.new_price.toFixed(2) }}</td>
                                <td :class="item.price_change >= 0 ? 'text-success' : 'text-danger'">
                                    {{ item.price_change >= 0 ? '+' : '' }}${{ item.price_change.toFixed(2) }}
                                </td>
                                <td :class="item.price_change_percentage >= 0 ? 'text-success' : 'text-danger'">
                                    {{ item.price_change_percentage >= 0 ? '+' : '' }}{{ item.price_change_percentage.toFixed(2) }}%
                                </td>
                                <td>
                                    <span v-if="item.priceReason" class="reason-badge" :title="item.priceReason.description">
                                        {{ item.priceReason.name }}
                                    </span>
                                    <span v-else-if="item.reasonCode" class="reason-badge">
                                        {{ item.reasonCode.name }}
                                    </span>
                                    <span v-else>-</span>
                                </td>
                                <td>{{ item.notes || '-' }}</td>
                                <td>
                                    <span v-if="item.user">
                                        {{ getUserDisplayName(item.user) }}
                                    </span>
                                    <span v-else>-</span>
                                </td>
                            </tr>
                        </template>
                    </CustomTable>
                </div>
            </div>
        </AllowPermission>
    </Card>
</template>

<script>
import PriceFetchApi from "../../../utils/fetch/price-fetch-api";
import CustomTable from "../../../components/layout/CustomTable.vue";
import Card from "../../../components/UI/Card.vue";
import BackNavigateBtn from "../../../components/UI/BackNavigateBtn.vue";
import { PERMISSION_CODES } from "../../../store/permission";
import AllowPermission from "../../../components/layout/AllowPermission.vue";
import { format } from "date-fns";

export default {
    components: {
        CustomTable,
        Card,
        BackNavigateBtn,
        AllowPermission
    },
    data() {
        return {
            loading: true,
            adjustment: {
                id: "",
                name: "",
                totalItems: 0,
                priceIncrease: 0,
                priceDecrease: 0,
                netChange: 0,
                createdAt: "",
                formattedCreatedAt: ""
            },
            adjustmentItems: [],
            PERMISSION_CODES
        };
    },
    computed: {
        adjustmentId() {
            return this.$route.params.id;
        }
    },
    async mounted() {
        await this.fetchAdjustmentDetails();
    },
    methods: {
        async fetchAdjustmentDetails() {
            try {
                this.loading = true;
                const data = await PriceFetchApi.getPriceAdjustmentDetails(this.adjustmentId);
                
                // Set the parent adjustment details
                this.adjustment = data.result;
                
                // Format the created date if needed
                if (this.adjustment.createdAt && !this.adjustment.formattedCreatedAt) {
                    this.adjustment.formattedCreatedAt = format(
                        new Date(this.adjustment.createdAt),
                        "MMM d, yyyy h:mm a"
                    );
                }
                
                // Set the adjustment items
                this.adjustmentItems = data.items || [];
                
                // Fetch user information for each item if user_id is available
                await this.loadUserInformation();
                
            } catch (error) {
                console.error("Error fetching price adjustment details:", error);
                this.$toast.error("Failed to load price adjustment details");
            } finally {
                this.loading = false;
            }
        },
        
        async loadUserInformation() {
            // Create a Set to store unique user IDs
            const userIds = new Set();
            
            // Collect all unique user IDs from items
            this.adjustmentItems.forEach(item => {
                if (item.user_id && !item.user) {
                    userIds.add(item.user_id);
                }
            });
            
            // If parent adjustment has user_id but no user object
            if (this.adjustment.user_id && !this.adjustment.user) {
                userIds.add(this.adjustment.user_id);
            }
            
            // Fetch user information for each unique user ID
            const userPromises = Array.from(userIds).map(async userId => {
                try {
                    const userData = await PriceFetchApi.getUserInfo(userId);
                    return { userId, userData: userData.result };
                } catch (error) {
                    console.error(`Error fetching user info for ${userId}:`, error);
                    return { userId, userData: null };
                }
            });
            
            // Wait for all user data to be fetched
            const userResults = await Promise.all(userPromises);
            
            // Create a map of user IDs to user data
            const userMap = userResults.reduce((map, { userId, userData }) => {
                if (userData) {
                    map[userId] = userData;
                }
                return map;
            }, {});
            
            // Assign user data to adjustment items
            this.adjustmentItems = this.adjustmentItems.map(item => {
                if (item.user_id && userMap[item.user_id]) {
                    return { ...item, user: userMap[item.user_id] };
                }
                return item;
            });
            
            // Assign user data to parent adjustment if needed
            if (this.adjustment.user_id && userMap[this.adjustment.user_id]) {
                this.adjustment.user = userMap[this.adjustment.user_id];
                this.adjustment.createdBy = this.getUserDisplayName(userMap[this.adjustment.user_id]);
            }
        },
        
        getUserDisplayName(user) {
            if (!user) return '-';
            
            if (user.firstName && user.lastName) {
                return `${user.firstName} ${user.lastName}`;
            } else if (user.username) {
                return user.username;
            } else if (user.email) {
                return user.email;
            } else if (user.id) {
                return `User ID: ${user.id.substring(0, 8)}...`;
            }
            
            return 'Unknown user';
        }
    }
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.adjustment-header {
    background-color: $lightHoverColor;
    padding: 20px;
    border-radius: 8px;
    border: 1px solid $lightBorderColor;
    
    h3 {
        color: $primaryColor;
        font-weight: 600;
    }
}

.adjustment-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    margin-top: 10px;
    
    .summary-item {
        background-color: $lightCardBg;
        padding: 8px 15px;
        border-radius: 6px;
        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        display: flex;
        align-items: center;
        border: 1px solid $lightBorderColor;
        
        span {
            color: rgba($textColor, 0.7);
            margin-right: 10px;
            font-size: 0.9rem;
        }
        
        strong {
            font-size: 1rem;
            color: $textColor;
            
            &.text-success {
                color: $successColor;
            }
            
            &.text-danger {
                color: $dangerColor;
            }
        }
    }
}

.reason-badge {
    display: inline-block;
    padding: 3px 8px;
    background-color: $lightHoverColor;
    border-radius: 4px;
    border: 1px solid $lightBorderColor;
    font-size: 0.85rem;
    color: $textColor;
    cursor: default;
}

/* Tokenized table paddings */
::v-deep table th,
::v-deep table td {
    padding: $tableCellPaddingY $tableCellPaddingX;
}

.dark .adjustment-header {
    background-color: lighten($darkCardBg, 4%);
    border-color: $darkBorderColor;
    h3 { color: $darkTextColor; }
}
.dark .adjustment-summary .summary-item {
    background-color: $darkCardBg;
    border-color: $darkBorderColor;
    span { color: rgba($darkTextColor, 0.8); }
    strong { color: $darkTextColor; }
    strong.text-success { color: $successColorDark; }
    strong.text-danger { color: $dangerColorDark; }
}
.dark .reason-badge {
    background-color: lighten($darkCardBg, 4%);
    border-color: $darkBorderColor;
    color: $darkTextColor;
}

@media (max-width: 768px) {
    .adjustment-summary {
        margin-top: 20px;
        width: 100%;
    }
}
</style>
